import React from 'react';
import { Box, Typography } from '@mui/material';
import { SCORE_DISPLAY } from '../../constants/director-constants';
import { formatScore, showsColor, showsNumber } from '../../utils/director-score';

// Paleta del semáforo del portal del director (Tailwind 400).
const BG = {
  green:    '#34D399',
  yellow:   '#FBBF24',
  red:      '#F87171',
  gray:     '#9CA3AF',
  ungraded: '#9CA3AF',
  unsynced: '#9CA3AF',
};

// Texto oscuro: los fondos son claros y el blanco no contrasta lo suficiente.
const ON_COLOR_TEXT = '#1F2937';

/**
 * Calificación de un evento o rúbrica en el portal del director.
 *
 * Reemplaza a los antiguos `ScoreCell` (dashboard) y `SemaforoBadge`
 * (detalle de evento). Según la configuración del campamento muestra el
 * bloque de color, el número, o el número sobre el color.
 */
export default function ScoreDisplay({
  score,
  maxPoints,
  semaforo,
  mode = SCORE_DISPLAY.SEMAFORO,
  format,
  compact = false,
}) {
  const status  = semaforo?.status || 'gray';
  const bg      = BG[status] ?? BG.gray;
  const noGrade = status === 'ungraded' || status === 'unsynced';

  const withNumber = showsNumber(mode);
  const withColor  = showsColor(mode);

  const text = noGrade ? null : formatScore(score, maxPoints, format);

  // Modo semáforo (o sin número que mostrar): bloque de color, como siempre.
  if (!withNumber || text === null) {
    if (!withColor) {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textAlign: compact ? 'right' : 'center',
            whiteSpace: 'nowrap',
          }}
        >
          —
        </Typography>
      );
    }
    return compact
      ? <Box sx={{ width: 32, height: 20, borderRadius: 1.5, backgroundColor: bg, flexShrink: 0 }} />
      : <Box sx={{ width: '100%', maxWidth: 50, minHeight: 28, borderRadius: 1.5, backgroundColor: bg, mx: 'auto' }} />;
  }

  // Sólo número, sin fondo de color.
  if (!withColor) {
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          textAlign: compact ? 'right' : 'center',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {text}
      </Typography>
    );
  }

  // Ambos: número sobre el color del semáforo.
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
        minWidth: compact ? 56 : 72,
        minHeight: compact ? 20 : 28,
        px: 0.75,
        borderRadius: 1.5,
        backgroundColor: bg,
        flexShrink: 0,
        ...(compact ? {} : { mx: 'auto' }),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: ON_COLOR_TEXT,
          whiteSpace: 'nowrap',
          lineHeight: 1.2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
